import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Dropdown, IDropdownProps } from 'core-components/library/dropdown'
import { Flex } from 'core-components/library/flex'
import { LoaderOverlay } from 'core-components/library/loader'
import { isEmpty } from 'core-components/library/validation'
import {
  FlexGridWrapperContext,
  IFlexGridWrapperContext,
} from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import { BaseCrud, Static } from 'core-components/types'

export interface IGroupSelector {
  value: string
  onChange: IDropdownProps['onChange']
}

export const GroupSelector = ({ value, onChange }: IGroupSelector) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [groupItems, setGroupItems] = useState<string[]>([])
  const { saveGroup, fetchWidgetGroups } =
    useContext<IFlexGridWrapperContext>(FlexGridWrapperContext)

  const groupsCrud = useMemo(() => {
    return new BaseCrud({
      fetcher: new Static(
        groupItems.map((group) => {
          return { value: group, label: group }
        })
      ),
      defaultPaging: {
        page: 0,
        pageSize: 100,
      },
      defaultSort: {
        field: 'value',
        dir: 'asc',
      },
      keyField: 'value',
    })
  }, [groupItems])

  const loadGroups = useCallback(() => {
    setLoading(true)
    fetchWidgetGroups()
      .then(setGroupItems)
      .finally(() => setLoading(false))
  }, [fetchWidgetGroups])

  const onCreate = useCallback(
    async (newGroup: string) => {
      if (saveGroup) {
        setLoading(true)
        await saveGroup(newGroup)
        await loadGroups()
        onChange(newGroup)
        setLoading(false)
      }
    },
    [saveGroup, onChange]
  )

  useEffect(() => {
    loadGroups()
  }, [loadGroups])

  return (
    <Flex className={'content-full-width context-relative'}>
      <Dropdown
        checkFn={loading ? undefined : isEmpty}
        classes={{ controlClasses: { root: 'content-full-width' } }}
        caption={'label'}
        source={groupsCrud}
        label="Group name"
        allowCreate={!!saveGroup}
        value={value}
        onChange={onChange}
        onCreate={onCreate}
      />
      <LoaderOverlay show={loading} dimension={{ width: 20, height: 20 }} />
    </Flex>
  )
}
